// Iter 54 · Fase 3 · AI Enrichment UI for /admin/checklist-master
// Modals: EnrichWithAIModal (generate) · ReviewSuggestionsModal (accept/reject/move/apply)
// Widget: RecentChangesWidget (collapsible top-of-page audit trail)

import React, { useEffect, useState, useMemo, useRef } from 'react';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    Sparkles, Loader2, X, Check, Trash2, ChevronDown, ChevronUp, ChevronRight, ExternalLink,
    Edit3, ArrowRightLeft, AlertTriangle, Clock, User as UserIcon, ScrollText, History,
} from 'lucide-react';

const ACCENT = '#7C6BEF';

// ─── Utility · Relative time in Spanish ─────────────────────────────────
function relTime(iso) {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(0, Math.round((now - then) / 1000));
    if (s < 60) return `hace ${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `hace ${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.round(h / 24);
    if (d < 30) return `hace ${d} d`;
    return new Date(iso).toLocaleDateString('es-MX');
}

// ────────────────────────────────────────────────────────────────────────
// 1) EnrichWithAIModal — kicks off Tavily+Gemini generation
// ────────────────────────────────────────────────────────────────────────
export function EnrichWithAIModal({ section, onClose, onGenerated }) {
    const [normCode, setNormCode] = useState('');
    const [normName, setNormName] = useState('');
    const [nItems, setNItems] = useState(5);
    const [extraContext, setExtraContext] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await api.post('/admin/checklist-master/ai-suggestions/generate', {
                target_section_id: section.section_id,
                reference_norm_code: normCode.trim(),
                reference_norm_name: normName.trim(),
                n_items_requested: Math.max(1, Math.min(parseInt(nItems, 10) || 5, 15)),
                extra_context: extraContext.trim(),
            });
            const persisted = (r.data.suggestions || []).length;
            const returned = r.data.ai_metadata?.questions_returned_by_llm ?? 0;
            const src = r.data.ai_metadata?.context_source;
            const age = r.data.ai_metadata?.cache_age_days;
            const provenanceLabel = src === 'cache'
                ? ` · Contexto desde caché (guardado hace ${age} d)`
                : src === 'tavily_live'
                ? ' · Contexto nuevo desde Tavily'
                : '';
            if (persisted === 0) {
                toast.warning(
                    r.data.warning || r.data.debug?.reason ||
                    'La IA no generó propuestas válidas. Revisa la norma de referencia o el contexto.',
                    { duration: 8000 }
                );
            } else {
                toast.success(`${persisted}/${returned} propuestas generadas para "${section.title}"${provenanceLabel}`);
            }
            onGenerated(r.data);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al generar propuestas'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}
            data-testid="enrich-ai-modal">
            <div className="w-full max-w-lg flex flex-col max-h-[calc(100vh-2rem)]" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3 p-6 pb-4 shrink-0">
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center" style={{ background: `${ACCENT}22`, color: ACCENT }}>
                        <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: ACCENT }}>Enriquecer con IA</div>
                        <h2 className="font-display font-black text-lg">{section.section_id} · {section.title}</h2>
                        <p className="text-xs opacity-70 mt-1">
                            Tavily buscará cláusulas reales de la norma y Gemini propondrá items nuevos. Solo se
                            guardan como propuestas — nada toca el master hasta que las apruebes.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1" data-testid="enrich-ai-close"><X className="w-4 h-4" /></button>
                </div>

                <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
                <div className="px-6 space-y-3 overflow-y-auto flex-1 min-h-0">
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold opacity-70">
                            Código de la norma <span className="opacity-50 normal-case">(opcional — la IA la infiere si dejas vacío)</span>
                        </label>
                        <input value={normCode} onChange={(e) => setNormCode(e.target.value)}
                            data-testid="enrich-norm-code"
                            placeholder="Ej. NFPA 25, NOM-002-STPS-2010"
                            className="w-full mt-1 px-3 py-2 border text-sm outline-none"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold opacity-70">Nombre completo (opcional)</label>
                        <input value={normName} onChange={(e) => setNormName(e.target.value)}
                            data-testid="enrich-norm-name"
                            placeholder="Ej. Standard for the Inspection of Water-Based Fire Protection Systems"
                            className="w-full mt-1 px-3 py-2 border text-sm outline-none"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold opacity-70">N° items</label>
                            <input type="number" min={1} max={15} value={nItems} onChange={(e) => setNItems(e.target.value)}
                                data-testid="enrich-n-items"
                                className="w-full mt-1 px-3 py-2 border text-sm outline-none"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                        </div>
                        <div className="col-span-2">
                            <label className="text-[10px] uppercase tracking-wider font-bold opacity-70">Contexto extra (opcional)</label>
                            <input value={extraContext} onChange={(e) => setExtraContext(e.target.value)}
                                data-testid="enrich-extra-context"
                                placeholder="Ej. enfócate en frecuencia de retrolavados"
                                className="w-full mt-1 px-3 py-2 border text-sm outline-none"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                        </div>
                    </div>
                </div>
                    <div className="flex justify-end gap-2 p-6 pt-3 border-t shrink-0" style={{ borderColor: 'var(--border)' }}>
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm border" style={{ borderColor: 'var(--border)' }}>Cancelar</button>
                        <button type="submit" disabled={loading}
                            data-testid="enrich-submit"
                            className="px-4 py-2 text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
                            style={{ background: ACCENT }}>
                            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando...</> : <><Sparkles className="w-4 h-4" /> Generar propuestas</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
// 2) ReviewSuggestionsModal — accept/reject/edit/move/apply
// ────────────────────────────────────────────────────────────────────────
export function ReviewSuggestionsModal({ section, allSections, batchId, onClose, onAppliedRefresh }) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({}); // {id: true}
    const [editing, setEditing] = useState({}); // {id: patch}
    const [scrolledToEnd, setScrolledToEnd] = useState(false);
    const [applying, setApplying] = useState(false);
    const listRef = useRef(null);

    const load = () => {
        setLoading(true);
        const params = { section_id: section.section_id, status: 'pending_review', limit: 100 };
        if (batchId) params.batch_id = batchId;
        api.get('/admin/checklist-master/ai-suggestions', { params })
            .then((r) => setSuggestions(r.data || []))
            .catch((e) => toast.error(apiErrorMessage(e, 'Error al cargar propuestas')))
            .finally(() => setLoading(false));
    };
    useEffect(load, [section.section_id, batchId]);

    // Iter 61bis · scroll-guard fix
    // The apply-in-bulk button used to require `scrolledToEnd = true`, but that
    // state was only set inside the `onScroll` handler — which never fires when
    // the list content is shorter than the container (nothing to scroll). Result:
    // 2 accepted proposals + short list ⇒ button stuck disabled forever.
    // Fix: recompute the "read to bottom" flag from the actual ref whenever the
    // list changes (accept/reject alters card count → container height shifts).
    // If there's no scroll possible ⇒ treat as already at the bottom.
    // Only runs AFTER loading finishes so we don't flip true on the empty
    // initial container.
    const checkScrollBottom = () => {
        const el = listRef.current;
        if (!el) return;
        const canScroll = el.scrollHeight > el.clientHeight + 1;
        if (!canScroll) {
            setScrolledToEnd(true);
            return;
        }
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
            setScrolledToEnd(true);
        }
    };
    const onScroll = () => checkScrollBottom();
    useEffect(() => {
        if (loading) return;  // wait for real content
        // Double RAF waits for layout to settle after suggestions mutate
        // (e.g. accepting a card changes overall content height).
        const raf1 = requestAnimationFrame(() => {
            requestAnimationFrame(checkScrollBottom);
        });
        return () => cancelAnimationFrame(raf1);
    }, [suggestions, loading]);

    const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
    const selectedIds = Object.keys(selected).filter((k) => selected[k]);

    const patchOne = async (id, body) => {
        try {
            const r = await api.patch(`/admin/checklist-master/ai-suggestions/${id}`, body);
            setSuggestions((prev) => prev.map((s) => (s.id === id ? r.data : s)));
            return r.data;
        } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo editar la propuesta'));
        }
    };

    const acceptOne = async (id) => {
        // Apply pending in-modal edits first (if any)
        if (editing[id]) {
            await patchOne(id, { proposed_item: editing[id] });
            setEditing((e) => { const c = { ...e }; delete c[id]; return c; });
        }
        try {
            const r = await api.post(`/admin/checklist-master/ai-suggestions/${id}/accept`);
            setSuggestions((prev) => prev.map((s) => (s.id === id ? r.data : s)));
            toast.success('Propuesta aceptada');
        } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo aceptar'));
        }
    };

    const rejectOne = async (id) => {
        const reason = prompt('Motivo del rechazo (opcional):') || '';
        try {
            const r = await api.post(`/admin/checklist-master/ai-suggestions/${id}/reject`, { reason });
            setSuggestions((prev) => prev.map((s) => (s.id === id ? r.data : s)));
            toast.success('Propuesta rechazada');
        } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo rechazar'));
        }
    };

    const acceptSelected = async () => {
        for (const id of selectedIds) {
            const s = suggestions.find((x) => x.id === id);
            if (s?.status === 'pending_review') await acceptOne(id);
        }
    };
    const rejectSelected = async () => {
        const reason = prompt('Motivo del rechazo en bloque (opcional):') || '';
        for (const id of selectedIds) {
            try {
                const r = await api.post(`/admin/checklist-master/ai-suggestions/${id}/reject`, { reason });
                setSuggestions((prev) => prev.map((s) => (s.id === id ? r.data : s)));
            } catch (err) { /* continue */ }
        }
        toast.success(`Rechazadas ${selectedIds.length}`);
    };

    const applyAllAccepted = async () => {
        const acceptedIds = suggestions.filter((s) => s.status === 'accepted').map((s) => s.id);
        if (acceptedIds.length === 0) {
            toast.info('No hay propuestas aceptadas para aplicar');
            return;
        }
        setApplying(true);
        try {
            const r = await api.post('/admin/checklist-master/ai-suggestions/apply', { suggestion_ids: acceptedIds });
            const applied = r.data.applied || [];
            const failed = r.data.failed || [];
            const bySection = applied.reduce((acc, x) => { acc[x.section_id] = (acc[x.section_id] || 0) + 1; return acc; }, {});
            const summary = Object.entries(bySection).map(([sid, n]) => `${sid}: ${n}`).join(' · ');
            if (applied.length) toast.success(`Aplicadas ${applied.length} propuestas al master → ${summary}`);
            if (failed.length) toast.error(`${failed.length} fallaron: ${failed.map((f) => f.error).slice(0, 2).join(', ')}`);
            onAppliedRefresh && onAppliedRefresh();
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al aplicar propuestas'));
        } finally {
            setApplying(false);
        }
    };

    const pendingCount = suggestions.filter((s) => s.status === 'pending_review').length;
    const acceptedCount = suggestions.filter((s) => s.status === 'accepted').length;
    const canApplyBulk = acceptedCount > 1 ? scrolledToEnd : acceptedCount === 1;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}
            data-testid="review-suggestions-modal">
            <div className="w-full max-w-3xl h-[85vh] flex flex-col"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
                onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="p-5 border-b flex items-start justify-between gap-3 shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: ACCENT }}>Revisar propuestas IA</div>
                        <h2 className="font-display font-black text-lg">{section.section_id} · {section.title}</h2>
                        <div className="mt-1 text-xs opacity-70">
                            {pendingCount} pendientes · {acceptedCount} aceptadas · {suggestions.filter((s) => s.status === 'rejected').length} rechazadas
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="review-close" className="p-1"><X className="w-5 h-5" /></button>
                </div>

                {/* List */}
                <div ref={listRef} onScroll={onScroll}
                    className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
                    {loading ? (
                        <div className="text-center py-8 opacity-60"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
                    ) : suggestions.length === 0 ? (
                        <div className="text-center py-16 opacity-60">
                            No hay propuestas pendientes para esta sección. Usa el botón <b>Enriquecer con IA</b> para generar.
                        </div>
                    ) : (
                        suggestions.map((s) => (
                            <SuggestionCard
                                key={s.id}
                                suggestion={s}
                                allSections={allSections}
                                selected={!!selected[s.id]}
                                onToggleSelect={() => toggleSelect(s.id)}
                                editingPatch={editing[s.id]}
                                onEdit={(patch) => setEditing((e) => ({ ...e, [s.id]: patch }))}
                                onSaveEdit={async (patch) => {
                                    await patchOne(s.id, { proposed_item: patch });
                                    setEditing((e) => { const c = { ...e }; delete c[s.id]; return c; });
                                }}
                                onMove={async (newSection) => {
                                    await patchOne(s.id, { target_section_id: newSection });
                                    toast.success(`Movida a ${newSection}`);
                                }}
                                onAccept={() => acceptOne(s.id)}
                                onReject={() => rejectOne(s.id)}
                            />
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t flex items-center justify-between gap-3 shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-2">
                        <span className="text-xs opacity-70">{selectedIds.length} seleccionadas</span>
                        <button onClick={acceptSelected} disabled={selectedIds.length === 0}
                            data-testid="review-accept-selected"
                            className="px-3 py-1.5 text-xs font-bold border disabled:opacity-40"
                            style={{ borderColor: '#10B981', color: '#10B981' }}>
                            <Check className="w-3 h-3 inline mr-1" /> Aceptar seleccionadas
                        </button>
                        <button onClick={rejectSelected} disabled={selectedIds.length === 0}
                            data-testid="review-reject-selected"
                            className="px-3 py-1.5 text-xs font-bold border disabled:opacity-40"
                            style={{ borderColor: '#EF4444', color: '#EF4444' }}>
                            <Trash2 className="w-3 h-3 inline mr-1" /> Rechazar seleccionadas
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        {acceptedCount > 1 && !scrolledToEnd && (
                            <span className="text-[10px] opacity-70 italic">Baja hasta el final para habilitar</span>
                        )}
                        <button onClick={applyAllAccepted} disabled={!canApplyBulk || applying || acceptedCount === 0}
                            data-testid="review-apply-all"
                            className="px-4 py-2 text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-40"
                            style={{ background: ACCENT }}>
                            {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Aplicando...</> : <>Aplicar {acceptedCount} al master</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SuggestionCard({ suggestion, allSections, selected, onToggleSelect, editingPatch, onEdit, onSaveEdit, onMove, onAccept, onReject }) {
    const p = suggestion.proposed_item || {};
    const [showFull, setShowFull] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [draft, setDraft] = useState({ ...p });
    const conf = suggestion.ai_metadata?.confidence ?? 0.6;
    const lowConf = conf < 0.6;
    const status = suggestion.status;
    const dup = suggestion.possibly_duplicate;
    const moved = suggestion.admin_edits?.moved_to_section && suggestion.admin_edits.moved_to_section !== (suggestion.admin_edits.moved_from_section || suggestion.target_section_id);
    const sources = suggestion.ai_metadata?.tavily_sources || [];

    const statusStyle = {
        pending_review: { bg: 'rgba(59,130,246,0.10)', color: '#3B82F6', label: 'Pendiente' },
        accepted: { bg: 'rgba(16,185,129,0.10)', color: '#10B981', label: 'Aceptada' },
        rejected: { bg: 'rgba(239,68,68,0.10)', color: '#EF4444', label: 'Rechazada' },
        applied: { bg: 'rgba(124,107,239,0.15)', color: ACCENT, label: 'Aplicada' },
    }[status] || { bg: '#EEE', color: '#333', label: status };

    return (
        <div className="border p-3" style={{ borderColor: 'var(--border)' }} data-testid={`suggestion-${suggestion.id}`}>
            {/* Header */}
            <div className="flex items-start gap-3">
                <input type="checkbox" checked={selected} onChange={onToggleSelect}
                    disabled={status === 'applied' || status === 'expired'}
                    data-testid={`suggestion-select-${suggestion.id}`}
                    className="mt-1" />
                <div className="flex-1 min-w-0">
                    {/* Badges */}
                    <div className="flex flex-wrap items-center gap-1 mb-2">
                        <span className="text-[10px] font-bold px-2 py-0.5" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                            {statusStyle.label}
                        </span>
                        <span className="text-[10px] font-mono px-2 py-0.5" style={{ background: 'rgba(0,0,0,0.05)' }}>
                            weight={p.weight}
                        </span>
                        {dup && (
                            <span className="text-[10px] font-bold px-2 py-0.5 inline-flex items-center gap-1"
                                style={{ background: 'rgba(234,179,8,0.15)', color: '#B45309' }}>
                                <AlertTriangle className="w-3 h-3" /> Posible duplicado
                            </span>
                        )}
                        {lowConf && (
                            <span className="text-[10px] font-bold px-2 py-0.5"
                                style={{ background: 'rgba(234,179,8,0.15)', color: '#B45309' }}>
                                Confianza baja ({conf.toFixed(2)})
                            </span>
                        )}
                        {moved && (
                            <span className="text-[10px] font-bold px-2 py-0.5 inline-flex items-center gap-1"
                                style={{ background: 'rgba(124,107,239,0.15)', color: ACCENT }}>
                                <ArrowRightLeft className="w-3 h-3" /> Movida
                            </span>
                        )}
                        {/* Iter 56 · cache provenance badge */}
                        {suggestion.ai_metadata?.context_source === 'cache' && (
                            <span className="text-[10px] font-bold px-2 py-0.5 inline-flex items-center gap-1"
                                data-testid={`suggestion-cache-${suggestion.id}`}
                                title={`Contexto reutilizado de la Biblioteca de Normativas (guardado hace ${suggestion.ai_metadata.cache_age_days ?? '?'} días)`}
                                style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>
                                <ScrollText className="w-3 h-3" /> Desde caché · {suggestion.ai_metadata.cache_age_days ?? '?'} d
                            </span>
                        )}
                        {suggestion.ai_metadata?.context_source === 'tavily_live' && (
                            <span className="text-[10px] font-bold px-2 py-0.5 inline-flex items-center gap-1"
                                data-testid={`suggestion-live-${suggestion.id}`}
                                title="Contexto obtenido en vivo de Tavily durante esta generación"
                                style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6' }}>
                                <ExternalLink className="w-3 h-3" /> Tavily nuevo
                            </span>
                        )}
                    </div>

                    {/* Editable question */}
                    {editMode ? (
                        <textarea value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })}
                            data-testid={`suggestion-edit-question-${suggestion.id}`}
                            className="w-full text-sm border p-2 outline-none"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                            rows={3} />
                    ) : (
                        <p className="text-sm leading-snug">{p.question}</p>
                    )}
                    <div className="mt-1 text-[11px] opacity-70 font-mono">📜 {p.standard_reference}</div>
                    {p.tooltip && <div className="mt-1 text-[11px] opacity-60 italic">💡 {p.tooltip}</div>}
                </div>
            </div>

            {/* Controls row */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
                {/* Move-to-section selector */}
                <label className="text-[10px] uppercase tracking-wider opacity-60 mr-1">Mover a:</label>
                <select value={suggestion.target_section_id}
                    onChange={(e) => onMove(e.target.value)}
                    disabled={status === 'applied' || status === 'expired'}
                    data-testid={`suggestion-move-${suggestion.id}`}
                    className="text-xs border px-2 py-1 outline-none"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    {(allSections || []).map((s) => (
                        <option key={s.section_id} value={s.section_id}>
                            {s.section_id} · {s.title || s.section_title}
                        </option>
                    ))}
                </select>

                <div className="ml-auto flex items-center gap-1">
                    {!editMode && status !== 'applied' && status !== 'expired' && (
                        <button onClick={() => setEditMode(true)}
                            data-testid={`suggestion-edit-toggle-${suggestion.id}`}
                            className="p-1.5 border" style={{ borderColor: 'var(--border)' }} title="Editar antes de aceptar">
                            <Edit3 className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {editMode && (
                        <>
                            <button onClick={async () => { await onSaveEdit(draft); setEditMode(false); }}
                                data-testid={`suggestion-edit-save-${suggestion.id}`}
                                className="text-[11px] px-2 py-1 border" style={{ borderColor: '#10B981', color: '#10B981' }}>
                                Guardar edición
                            </button>
                            <button onClick={() => { setDraft({ ...p }); setEditMode(false); }}
                                className="text-[11px] px-2 py-1 border" style={{ borderColor: 'var(--border)' }}>
                                Cancelar
                            </button>
                        </>
                    )}
                    {status === 'pending_review' && (
                        <>
                            <button onClick={onAccept} data-testid={`suggestion-accept-${suggestion.id}`}
                                className="text-[11px] px-2 py-1 border font-bold inline-flex items-center gap-1"
                                style={{ borderColor: '#10B981', color: '#10B981' }}>
                                <Check className="w-3 h-3" /> Aceptar
                            </button>
                            <button onClick={onReject} data-testid={`suggestion-reject-${suggestion.id}`}
                                className="text-[11px] px-2 py-1 border font-bold inline-flex items-center gap-1"
                                style={{ borderColor: '#EF4444', color: '#EF4444' }}>
                                <Trash2 className="w-3 h-3" /> Rechazar
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Sources */}
            {sources.length > 0 && (
                <details className="mt-2">
                    <summary className="text-[10px] uppercase tracking-wider opacity-70 cursor-pointer">
                        Fuentes Tavily ({sources.length})
                    </summary>
                    <div className="mt-1 flex flex-wrap gap-1">
                        {sources.slice(0, 8).map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noreferrer"
                                data-testid={`suggestion-source-${suggestion.id}-${i}`}
                                className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 border truncate max-w-[280px]"
                                style={{ borderColor: 'var(--border)' }}>
                                <ExternalLink className="w-2.5 h-2.5" /> {new URL(url).hostname}
                            </a>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────────
// 3) RecentChangesWidget — collapsible, top-of-page
// ────────────────────────────────────────────────────────────────────────
export function RecentChangesWidget() {
    const [open, setOpen] = useState(false);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get('/admin/checklist-master/change-log?limit=10'),
            api.get('/admin/checklist-master/ai-suggestions/stats').catch(() => ({ data: null })),
        ]).then(([logR, statsR]) => {
            setEntries(logR.data || []);
            setStats(statsR.data);
        }).catch(() => { /* silent */ }).finally(() => setLoading(false));
    };

    useEffect(() => { if (open) load(); }, [open]);

    return (
        <section className="mb-4" data-testid="recent-changes-widget">
            <button type="button" onClick={() => setOpen((v) => !v)}
                data-testid="recent-changes-toggle"
                className="w-full flex items-center justify-between px-4 py-3 border text-left"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <div className="flex items-center gap-2">
                    <History className="w-4 h-4" style={{ color: ACCENT }} />
                    <span className="text-sm font-bold">Últimos cambios al master</span>
                    {stats?.pending > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5"
                            style={{ background: `${ACCENT}22`, color: ACCENT }}>
                            {stats.pending} propuestas IA pendientes
                        </span>
                    )}
                </div>
                {open ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
            </button>
            {open && (
                <div className="border-t-0 border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    {loading ? (
                        <div className="p-4 text-center opacity-60"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
                    ) : entries.length === 0 ? (
                        <div className="p-4 text-center text-xs opacity-60">Sin cambios registrados aún.</div>
                    ) : (
                        <ul className="divide-y" style={{ borderColor: 'var(--border)' }}>
                            {entries.map((e) => (
                                <ChangeLogEntry key={e.id} entry={e} />
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
}

function ChangeLogEntry({ entry }) {
    const isAi = entry.source === 'ai_suggestion';
    const sectionId = entry.target?.section_id;
    const itemId = entry.target?.item_id;
    const email = entry.actor?.admin_email || entry.actor?.admin_id || '—';
    const norm = entry.reference_norm?.code;
    const movedFrom = entry.after?.moved_from_section;
    const typeLabel = {
        item_added: 'Agregado',
        item_edited: 'Editado',
        item_deleted: 'Eliminado',
        section_added: 'Sección nueva',
    }[entry.change_type] || entry.change_type;

    return (
        <li className="px-4 py-2.5 flex items-start gap-3" data-testid={`changelog-entry-${entry.id}`}>
            <span className="mt-0.5 shrink-0 w-6 h-6 flex items-center justify-center"
                title={isAi ? 'IA · Sugerencia aplicada' : 'Cambio manual del administrador'}
                style={{ background: isAi ? `${ACCENT}22` : 'rgba(0,0,0,0.05)', color: isAi ? ACCENT : '#444' }}>
                {isAi ? <Sparkles className="w-3 h-3" /> : <Edit3 className="w-3 h-3" />}
            </span>
            <div className="flex-1 min-w-0">
                <div className="text-xs flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{typeLabel}</span>
                    <span className="opacity-70">·</span>
                    <span className="font-mono text-[11px] opacity-80">{sectionId}{itemId ? ` › ${itemId}` : ''}</span>
                    {movedFrom && (
                        <span className="text-[10px] px-1.5 py-0.5 inline-flex items-center gap-1"
                            style={{ background: `${ACCENT}18`, color: ACCENT }}>
                            <ArrowRightLeft className="w-2.5 h-2.5" /> Movido desde {movedFrom}
                        </span>
                    )}
                    {norm && (
                        <span className="text-[10px] px-1.5 py-0.5 font-mono"
                            style={{ background: 'rgba(0,0,0,0.05)' }}>
                            {norm}
                        </span>
                    )}
                </div>
                {entry.after?.question && (
                    <div className="text-[11px] opacity-70 truncate mt-0.5">{entry.after.question}</div>
                )}
                <div className="text-[10px] opacity-60 mt-0.5 flex items-center gap-2">
                    <UserIcon className="w-2.5 h-2.5" /> {email}
                    <Clock className="w-2.5 h-2.5 ml-1" /> {relTime(entry.applied_at)}
                    {isAi ? (
                        <span className="ml-1 font-bold" style={{ color: ACCENT }}>IA</span>
                    ) : (
                        <span className="ml-1 opacity-70">Manual</span>
                    )}
                </div>
            </div>
        </li>
    );
}
